<template>
	<div class="refund-application-container">
		<!-- 页面头部 -->
		<div class="page-header">
			<div class="header-left">
				<h1>退款申请管理</h1>
				<p>管理系统退款申请，包含审核、批准、拒绝等功能</p>
			</div>
			<div class="header-actions">
				<el-button type="primary" icon="el-icon-refresh" @click="search()">
					刷新数据
				</el-button>
			</div>
		</div>

		<!-- 筛选条件 -->
		<div class="filter-section">
			<el-card class="filter-card">
				<div class="filter-content">
					<div class="filter-group">
						<span class="filter-label">申请状态：</span>
						<el-radio-group v-model="dataForm.status" @change="search">
							<el-radio-button label="">全部</el-radio-button>
							<el-radio-button label="待审核">待审核</el-radio-button>
							<el-radio-button label="已通过">已通过</el-radio-button>
							<el-radio-button label="审核通过">审核通过</el-radio-button>
							<el-radio-button label="已拒绝">已拒绝</el-radio-button>
							<el-radio-button label="已退款">已退款</el-radio-button>
							<el-radio-button label="退款失败">退款失败</el-radio-button>
							<el-radio-button label="已取消">已取消</el-radio-button>
						</el-radio-group>
					</div>
					
					<div class="filter-group">
						<span class="filter-label">退款类型：</span>
						<el-select v-model="dataForm.refundType" placeholder="全部类型" clearable @change="search">
							<el-option label="仅退款" value="仅退款"></el-option>
							<el-option label="退货退款" value="退货退款"></el-option>
						</el-select>
					</div>
					
					<div class="filter-group">
						<span class="filter-label">申请编号：</span>
						<el-input
							v-model="dataForm.applicationNo"
							placeholder="请输入申请编号"
							style="width: 200px;"
							clearable
							@keyup.enter.native="search"
						></el-input>
					</div>
					
					<div class="filter-group">
						<span class="filter-label">订单编号：</span>
						<el-input
							v-model="dataForm.orderid"
							placeholder="请输入订单编号"
							style="width: 200px;"
							clearable
							@keyup.enter.native="search"
						></el-input>
					</div>
					
					<div class="filter-actions">
						<el-button type="primary" @click="search">搜索</el-button>
						<el-button @click="resetSearch">重置</el-button>
					</div>
				</div>
			</el-card>
		</div>

		<!-- 数据表格 -->
		<div class="table-section">
			<el-card class="table-card">
				<el-table
					:data="dataList"
					border
					v-loading="dataListLoading"
					style="width: 100%"
					@selection-change="selectionChangeHandler"
				>
					<el-table-column type="selection" width="50" align="center"></el-table-column>
					
					<el-table-column prop="applicationNo" label="申请编号" width="180" show-overflow-tooltip>
						<template slot-scope="scope">
							<div class="application-no">
								<i class="el-icon-document"></i>
								<span>{{ scope.row.applicationNo }}</span>
							</div>
						</template>
					</el-table-column>
					
					<el-table-column prop="orderid" label="订单编号" width="180" show-overflow-tooltip>
						<template slot-scope="scope">
							<div class="order-id">
								<i class="el-icon-shopping-bag-2"></i>
								<span>{{ scope.row.orderid }}</span>
							</div>
						</template>
					</el-table-column>
					
					<el-table-column prop="username" label="申请用户" width="120" align="center">
						<template slot-scope="scope">
							<div class="user-info">
								<i class="el-icon-user"></i>
								<span>{{ scope.row.username || '未知用户' }}</span>
							</div>
						</template>
					</el-table-column>
					
					<el-table-column prop="refundType" label="退款类型" width="100" align="center">
						<template slot-scope="scope">
							<el-tag :type="scope.row.refundType === '仅退款' ? 'primary' : 'warning'" size="small">
								{{ scope.row.refundType }}
							</el-tag>
						</template>
					</el-table-column>
					
					<el-table-column prop="refundAmount" label="退款金额" width="120" align="center">
						<template slot-scope="scope">
							<div class="amount">
								<span class="currency">￥</span>
								<span class="value">{{ scope.row.refundAmount.toFixed(2) }}</span>
							</div>
						</template>
					</el-table-column>
					
					<el-table-column prop="refundReason" label="退款原因" width="150" show-overflow-tooltip></el-table-column>
					
					<el-table-column prop="status" label="状态" width="100" align="center">
						<template slot-scope="scope">
							<el-tag :type="getStatusType(scope.row.status)" size="small">
								{{ scope.row.status }}
							</el-tag>
						</template>
					</el-table-column>
					
					<el-table-column prop="addtime" label="申请时间" width="160" align="center">
						<template slot-scope="scope">
							<div class="time-info">
								<i class="el-icon-time"></i>
								<span>{{ parseTime(scope.row.addtime) }}</span>
							</div>
						</template>
					</el-table-column>
					
					<el-table-column prop="processTime" label="处理时间" width="160" align="center">
						<template slot-scope="scope">
							<div class="time-info" v-if="scope.row.processTime">
								<i class="el-icon-check"></i>
								<span>{{ parseTime(scope.row.processTime) }}</span>
							</div>
							<span v-else class="no-process">未处理</span>
						</template>
					</el-table-column>
					
					<el-table-column label="操作" width="300" align="center" fixed="right">
						<template slot-scope="scope">
							<el-button type="info" size="mini" @click="viewDetail(scope.row)">
								<i class="el-icon-view"></i>
								详情
							</el-button>
							
							<el-button 
								v-if="scope.row.status === '待审核'" 
								type="success" 
								size="mini" 
								@click="approveRefund(scope.row)"
							>
								<i class="el-icon-check"></i>
								通过
							</el-button>
							
							<el-button 
								v-if="scope.row.status === '待审核'" 
								type="danger" 
								size="mini" 
								@click="rejectRefund(scope.row)"
							>
								<i class="el-icon-close"></i>
								拒绝
							</el-button>
							
							<el-button 
								v-if="scope.row.status === '已通过' || scope.row.status === '退款失败' || scope.row.status === '审核通过'" 
								type="warning" 
								size="mini" 
								@click="processRefund(scope.row)"
							>
								<i class="el-icon-money"></i>
								{{ scope.row.status === '退款失败' ? '重新退款' : (scope.row.status === '审核通过' || scope.row.status === '已通过') ? '完成退款' : '退款' }}
							</el-button>
						</template>
					</el-table-column>
				</el-table>
				
				<!-- 分页 -->
				<el-pagination
					@size-change="sizeChangeHandler"
					@current-change="currentChangeHandler"
					:current-page="pageIndex"
					:page-sizes="[10, 20, 50, 100]"
					:page-size="pageSize"
					:total="totalPage"
					layout="total, sizes, prev, pager, next, jumper"
					class="pagination"
				></el-pagination>
			</el-card>
		</div>

		<!-- 详情对话框 -->
		<el-dialog 
			title="退款申请详情" 
			:visible.sync="detailDialogVisible" 
			width="800px"
			@close="closeDetailDialog"
		>
			<div v-if="currentApplication" class="detail-content">
				<!-- 基本信息 -->
				<div class="detail-section">
					<h3 class="section-title">
						<i class="el-icon-document"></i>
						基本信息
					</h3>
					<div class="info-grid">
						<div class="info-item">
							<label>申请编号：</label>
							<span>{{ currentApplication.applicationNo }}</span>
						</div>
						<div class="info-item">
							<label>订单编号：</label>
							<span>{{ currentApplication.orderid }}</span>
						</div>
						<div class="info-item">
							<label>申请用户：</label>
							<span>{{ currentApplication.username }}</span>
						</div>
						<div class="info-item">
							<label>退款类型：</label>
							<el-tag :type="currentApplication.refundType === '仅退款' ? 'primary' : 'warning'">
								{{ currentApplication.refundType }}
							</el-tag>
						</div>
						<div class="info-item">
							<label>退款金额：</label>
							<span class="amount-large">￥{{ currentApplication.refundAmount.toFixed(2) }}</span>
						</div>
						<div class="info-item">
							<label>申请状态：</label>
							<el-tag :type="getStatusType(currentApplication.status)">
								{{ currentApplication.status }}
							</el-tag>
						</div>
					</div>
				</div>

				<!-- 申请详情 -->
				<div class="detail-section">
					<h3 class="section-title">
						<i class="el-icon-chat-line-square"></i>
						申请详情
					</h3>
					<div class="detail-info">
						<div class="info-item full-width">
							<label>退款原因：</label>
							<span>{{ currentApplication.refundReason }}</span>
						</div>
						<div class="info-item full-width" v-if="currentApplication.refundDescription">
							<label>问题描述：</label>
							<div class="description">{{ currentApplication.refundDescription }}</div>
						</div>
					</div>
				</div>

				<!-- 证据图片 -->
				<div class="detail-section" v-if="currentApplication.evidenceImages">
					<h3 class="section-title">
						<i class="el-icon-picture"></i>
						证据图片
					</h3>
					<div class="evidence-images">
						<div 
							v-for="(image, index) in getEvidenceImages(currentApplication.evidenceImages)" 
							:key="index"
							class="image-item"
						>
							<el-image
								:src="image"
								:preview-src-list="getEvidenceImages(currentApplication.evidenceImages)"
								fit="cover"
								class="evidence-img"
							></el-image>
						</div>
					</div>
				</div>

				<!-- 处理信息 -->
				<div class="detail-section" v-if="currentApplication.processTime">
					<h3 class="section-title">
						<i class="el-icon-user-solid"></i>
						处理信息
					</h3>
					<div class="process-info">
						<div class="info-item">
							<label>处理时间：</label>
							<span>{{ parseTime(currentApplication.processTime) }}</span>
						</div>
						<div class="info-item" v-if="currentApplication.adminRemark">
							<label>管理员备注：</label>
							<div class="admin-remark">{{ currentApplication.adminRemark }}</div>
						</div>
					</div>
				</div>
			</div>
			
			<div slot="footer" class="dialog-footer">
				<el-button @click="closeDetailDialog">关闭</el-button>
				<el-button 
					v-if="currentApplication && currentApplication.status === '待审核'" 
					type="success" 
					@click="approveRefund(currentApplication)"
				>
					通过申请
				</el-button>
				<el-button 
					v-if="currentApplication && currentApplication.status === '待审核'" 
					type="danger" 
					@click="rejectRefund(currentApplication)"
				>
					拒绝申请
				</el-button>
			</div>
		</el-dialog>

		<!-- 审核对话框 -->
		<el-dialog 
			:title="reviewAction === 'approve' ? '通过退款申请' : '拒绝退款申请'" 
			:visible.sync="reviewDialogVisible" 
			width="500px"
		>
			<el-form :model="reviewForm" label-width="100px">
				<el-form-item label="管理员备注">
					<el-input
						type="textarea"
						v-model="reviewForm.adminRemark"
						:placeholder="reviewAction === 'approve' ? '请输入通过原因（可选）' : '请输入拒绝原因'"
						:rows="4"
					></el-input>
				</el-form-item>
			</el-form>
			
			<div slot="footer" class="dialog-footer">
				<el-button @click="closeReviewDialog">取消</el-button>
				<el-button 
					:type="reviewAction === 'approve' ? 'success' : 'danger'" 
					@click="confirmReview"
					:loading="submitLoading"
				>
					确认{{ reviewAction === 'approve' ? '通过' : '拒绝' }}
				</el-button>
			</div>
		</el-dialog>
	</div>
</template>

<script>
export default {
	data() {
		return {
			dataForm: {
				status: '',
				refundType: '',
				applicationNo: '',
				orderid: ''
			},
			dataList: [],
			dataListLoading: false,
			dataListSelections: [],
			pageIndex: 1,
			pageSize: 10,
			totalPage: 0,
			
			// 详情对话框
			detailDialogVisible: false,
			currentApplication: null,
			
			// 审核对话框
			reviewDialogVisible: false,
			reviewAction: '', // 'approve' 或 'reject'
			reviewForm: {
				adminRemark: ''
			},
			submitLoading: false
		}
	},
	
	activated() {
		this.getDataList();
	},
	
	methods: {
		// 获取数据列表
		getDataList() {
			this.dataListLoading = true;
			let params = {
				page: this.pageIndex,
				limit: this.pageSize
			};
			
			// 添加筛选条件
			if (this.dataForm.status) {
				params.status = this.dataForm.status;
			}
			if (this.dataForm.refundType) {
				params.refundType = this.dataForm.refundType;
			}
			if (this.dataForm.applicationNo) {
				params.applicationNo = this.dataForm.applicationNo;
			}
			if (this.dataForm.orderid) {
				params.orderid = this.dataForm.orderid;
			}
			
			this.$http({
				url: "refundapplication/page",
				method: "get",
				params: params
			}).then(({ data }) => {
				if (data && data.code === 0) {
					this.dataList = data.data.list || [];
					this.totalPage = data.data.total || 0;
				} else {
					this.dataList = [];
					this.totalPage = 0;
				}
				this.dataListLoading = false;
			}).catch(() => {
				this.dataListLoading = false;
			});
		},
		
		// 搜索
		search() {
			this.pageIndex = 1;
			this.getDataList();
		},
		
		// 重置搜索
		resetSearch() {
			this.dataForm = {
				status: '',
				refundType: '',
				applicationNo: '',
				orderid: ''
			};
			this.search();
		},
		
		// 页面大小改变
		sizeChangeHandler(val) {
			this.pageSize = val;
			this.pageIndex = 1;
			this.getDataList();
		},
		
		// 当前页改变
		currentChangeHandler(val) {
			this.pageIndex = val;
			this.getDataList();
		},
		
		// 选择改变
		selectionChangeHandler(val) {
			this.dataListSelections = val;
		},
		
		// 查看详情
		viewDetail(row) {
			this.currentApplication = row;
			this.detailDialogVisible = true;
		},
		
		// 关闭详情对话框
		closeDetailDialog() {
			this.detailDialogVisible = false;
			this.currentApplication = null;
		},
		
		// 通过申请
		approveRefund(row) {
			this.currentApplication = row;
			this.reviewAction = 'approve';
			this.reviewForm.adminRemark = '';
			this.reviewDialogVisible = true;
		},
		
		// 拒绝申请
		rejectRefund(row) {
			this.currentApplication = row;
			this.reviewAction = 'reject';
			this.reviewForm.adminRemark = '';
			this.reviewDialogVisible = true;
		},
		
		// 关闭审核对话框
		closeReviewDialog() {
			this.reviewDialogVisible = false;
			this.reviewAction = '';
			this.reviewForm.adminRemark = '';
			this.currentApplication = null;
		},
		
		// 确认审核
		confirmReview() {
			if (this.reviewAction === 'reject' && !this.reviewForm.adminRemark.trim()) {
				this.$message.error('拒绝申请时必须填写拒绝原因');
				return;
			}
			
			this.submitLoading = true;
			const status = this.reviewAction === 'approve' ? '已通过' : '已拒绝';
			
			this.$http({
				url: "refundapplication/approve",
				method: "post",
				data: {
					id: this.currentApplication.id,
					status: status,
					adminRemark: this.reviewForm.adminRemark
				}
			}).then(({ data }) => {
				if (data && data.code === 0) {
					this.$message.success(`${this.reviewAction === 'approve' ? '通过' : '拒绝'}申请成功`);
					this.closeReviewDialog();
					this.detailDialogVisible = false;
					this.getDataList();
				} else {
					this.$message.error(data.msg);
				}
				this.submitLoading = false;
			}).catch(() => {
				this.submitLoading = false;
			});
		},
		
		// 处理退款
		processRefund(row) {
			const isComplete = row.status === '审核通过' || row.status === '已通过';
			const actionText = isComplete ? '完成退款' : '处理退款';
			const confirmText = isComplete ? 
				`确定手动完成申请编号 ${row.applicationNo} 的退款吗？\n请确保已通过其他方式完成退款操作。` : 
				`确定为申请编号 ${row.applicationNo} 处理退款吗？`;
			
			this.$confirm(confirmText, '退款确认', {
				confirmButtonText: `确定${actionText}`,
				cancelButtonText: '取消',
				type: 'warning'
			}).then(() => {
				if (isComplete) {
					// 调用完成退款接口
					this.$http({
						url: "refundapplication/completeRefund",
						method: "post",
						data: {
							refundId: row.id,
							adminRemark: '管理员确认已手动完成退款'
						}
					}).then(({ data }) => {
						if (data && data.code === 0) {
							this.$message.success('退款完成成功');
							this.getDataList();
						} else {
							this.$message.error(data.msg || '退款完成失败');
						}
					}).catch((error) => {
						console.error('退款完成异常:', error);
						this.$message.error('退款完成异常，请重试');
					});
				} else {
					// 调用退款审核接口，直接通过并执行退款
					this.$http({
						url: "refundapplication/review",
						method: "post",
						data: {
							refundId: row.id,
							action: 'approve',
							adminRemark: '管理员手动执行退款'
						}
					}).then(({ data }) => {
						if (data && data.code === 0) {
							this.$message.success('退款处理成功');
							this.getDataList();
						} else {
							this.$message.error(data.msg || '退款处理失败');
						}
					}).catch((error) => {
						console.error('退款处理异常:', error);
						this.$message.error('退款处理异常，请重试');
					});
				}
			});
		},
		
		// 获取状态类型
		getStatusType(status) {
			const statusMap = {
				'待审核': 'warning',
				'已通过': 'success',
				'已拒绝': 'danger',
				'已退款': 'info',
				'已取消': 'info',
				'退款失败': 'danger',
				'审核通过': 'success' // 新增审核通过状态
			};
			return statusMap[status] || '';
		},
		
		// 获取证据图片列表
		getEvidenceImages(evidenceImages) {
			if (!evidenceImages) return [];
			return evidenceImages.split(',').filter(img => img.trim());
		},
		
		// 格式化时间
		parseTime(time) {
			if (!time) return '';
			const date = new Date(time);
			return date.toLocaleString('zh-CN');
		}
	}
}
</script>

<style lang="scss" scoped>
.refund-application-container {
	padding: 20px;
	background: #f5f7fa;
	min-height: 100vh;
}

// 页面头部
.page-header {
	display: flex;
	justify-content: space-between;
	align-items: flex-start;
	margin-bottom: 20px;
	
	.header-left {
		h1 {
			margin: 0 0 8px 0;
			font-size: 24px;
			color: #303133;
			font-weight: 600;
		}
		
		p {
			margin: 0;
			color: #606266;
			font-size: 14px;
		}
	}
}

// 筛选区域
.filter-section {
	margin-bottom: 20px;
	
	.filter-card {
		.filter-content {
			display: flex;
			flex-wrap: wrap;
			gap: 20px;
			align-items: center;
			
			.filter-group {
				display: flex;
				align-items: center;
				gap: 10px;
				
				.filter-label {
					font-weight: 500;
					color: #606266;
					white-space: nowrap;
				}
			}
			
			.filter-actions {
				margin-left: auto;
			}
		}
	}
}

// 表格区域
.table-section {
	.table-card {
		.application-no,
		.order-id {
			display: flex;
			align-items: center;
			gap: 6px;
			
			i {
				color: #409eff;
			}
		}
		
		.user-info {
			display: flex;
			align-items: center;
			gap: 4px;
			
			i {
				color: #67c23a;
			}
		}
		
		.amount {
			font-weight: 600;
			
			.currency {
				color: #f56c6c;
			}
			
			.value {
				color: #303133;
			}
		}
		
		.time-info {
			display: flex;
			align-items: center;
			gap: 4px;
			font-size: 13px;
			
			i {
				color: #909399;
			}
		}
		
		.no-process {
			color: #c0c4cc;
			font-style: italic;
		}
		
		.pagination {
			margin-top: 20px;
			text-align: center;
		}
	}
}

// 详情对话框
.detail-content {
	.detail-section {
		margin-bottom: 25px;
		
		&:last-child {
			margin-bottom: 0;
		}
		
		.section-title {
			display: flex;
			align-items: center;
			gap: 8px;
			margin-bottom: 15px;
			font-size: 16px;
			font-weight: 600;
			color: #303133;
			border-bottom: 1px solid #ebeef5;
			padding-bottom: 8px;
			
			i {
				color: #409eff;
			}
		}
		
		.info-grid {
			display: grid;
			grid-template-columns: 1fr 1fr;
			gap: 15px;
		}
		
		.detail-info {
			display: flex;
			flex-direction: column;
			gap: 15px;
		}
		
		.info-item {
			display: flex;
			align-items: flex-start;
			
			&.full-width {
				grid-column: 1 / -1;
				flex-direction: column;
				gap: 8px;
			}
			
			label {
				font-weight: 500;
				color: #606266;
				min-width: 80px;
				margin-right: 10px;
			}
			
			span {
				color: #303133;
			}
			
			.amount-large {
				font-size: 18px;
				font-weight: 600;
				color: #f56c6c;
			}
			
			.description {
				background: #f8f9fa;
				padding: 12px;
				border-radius: 6px;
				border: 1px solid #e9ecef;
				line-height: 1.6;
			}
		}
		
		.evidence-images {
			display: grid;
			grid-template-columns: repeat(auto-fill, minmax(120px, 1fr));
			gap: 15px;
			
			.image-item {
				.evidence-img {
					width: 120px;
					height: 120px;
					border-radius: 8px;
					border: 1px solid #dcdfe6;
					cursor: pointer;
					transition: all 0.3s ease;
					
					&:hover {
						transform: scale(1.05);
						box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
					}
				}
			}
		}
		
		.process-info {
			.info-item {
				margin-bottom: 15px;
				
				&:last-child {
					margin-bottom: 0;
				}
				
				.admin-remark {
					background: #f0f9ff;
					padding: 12px;
					border-radius: 6px;
					border-left: 4px solid #409eff;
					line-height: 1.6;
				}
			}
		}
	}
}

// 响应式设计
@media (max-width: 768px) {
	.filter-content {
		flex-direction: column;
		align-items: stretch !important;
		
		.filter-group {
			flex-direction: column;
			align-items: stretch;
		}
		
		.filter-actions {
			margin-left: 0 !important;
		}
	}
	
	.info-grid {
		grid-template-columns: 1fr !important;
	}
}
</style> 